/**
 * 1:1 한 턴의 상태 (히어로챗 Chat.jsx 의 흐름).
 *
 *   보내기 → accepted(내 말 확정) → thinking(작성 중) → delta…(흘러나옴) → done
 *
 * - 중단: 서버에 stop 을 보냅니다. 서버가 stopped 로 답합니다.
 *   saved:false 면 내 말을 거두고 입력창으로 돌려놓습니다(onRestoreDraft)
 * - 끊김(done 없이 끝남)이나 오류면 대화를 다시 불러와 서버 상태로 맞춥니다(onResync)
 * - 화면을 떠나면 요청을 끊습니다. 서버는 그때까지 나온 만큼 저장합니다
 * - 같은 말을 다시 보낼 때는 같은 clientKey 를 써서 두 번 저장되지 않게 합니다
 * - 답 뒤에 붙는 말(이모의 사주 재요청 등)은 한꺼번에 오지만, 같은 멤버 연속 말풍선 간격(0.8~1.2초 × 말 속도)으로
 *   「입력 중」을 띄우며 하나씩 꺼냅니다(C-37 · D100). 위기 안내는 바로
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { streamChatTurn } from '../../api/chatStream.js';
import { errorMessageOf } from '../../api/client.js';
import { stopChatTurn } from '../../api/chat.js';
import { revealGapOf } from '../../stores/revealSpeed.js';

function wait(ms, signal) {
    return new Promise((resolve) => {
        if (signal.aborted) {
            resolve();
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal.addEventListener('abort', () => {
            clearTimeout(timer);
            resolve();
        });
    });
}

export function useChatTurn(memberKey, { onUserMessage, onMemberMessage, onRestoreDraft, onResync, onChips = null }) {
    const [phase, setPhase] = useState('idle');
    const [streamText, setStreamText] = useState('');
    // 서버가 받기 전까지 화면에 먼저 보이는 내 말 (내 말 → 입력 중 순서, 사용자 지정 2026-10-13)
    const [pendingText, setPendingText] = useState(null);
    const [error, setError] = useState(null);
    const controllerRef = useRef(null);
    const pendingRef = useRef(null);
    const handlersRef = useRef({ onUserMessage, onMemberMessage, onRestoreDraft, onResync, onChips });
    handlersRef.current = { onUserMessage, onMemberMessage, onRestoreDraft, onResync, onChips };

    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
        };
    }, [memberKey]);

    const send = useCallback(
        async (text, { chipItemsId = null } = {}) => {
            if (controllerRef.current) {
                return;
            }
            // 보내면 칩 줄은 바로 꺼집니다(23.7)
            handlersRef.current.onChips?.(null);
            const pending = pendingRef.current;
            const clientKey = pending?.text === text ? pending.clientKey : crypto.randomUUID();
            pendingRef.current = { text, clientKey };

            const controller = new AbortController();
            controllerRef.current = controller;
            setPhase('sending');
            setPendingText(text);
            setStreamText('');
            setError(null);

            const handlers = handlersRef.current;
            let acceptedMessagesId = null;
            // 답 뒤에 붙는 말과 칩은 답을 보인 뒤 차례로
            let followUps = [];
            let lastShown = null;
            let chips;
            try {
                await streamChatTurn(
                    memberKey,
                    chipItemsId ? { body: text, clientKey, chipItemsId } : { body: text, clientKey },
                    {
                        signal: controller.signal,
                        onEvent(name, data) {
                            if (name === 'accepted') {
                                pendingRef.current = null;
                                acceptedMessagesId = data.userMessage.messagesId;
                                handlers.onUserMessage(data.userMessage);
                                setPendingText(null);
                                setPhase('thinking');
                            } else if (name === 'thinking') {
                                setPhase('thinking');
                            } else if (name === 'delta') {
                                setPhase('streaming');
                                setStreamText((previous) => previous + data.text);
                            } else if (name === 'done') {
                                handlers.onMemberMessage(data.message);
                                lastShown = data.message;
                                if (data.safetyMessage) {
                                    handlers.onMemberMessage(data.safetyMessage);
                                    lastShown = data.safetyMessage;
                                }
                                // 이모의 사주 재요청처럼 답 뒤에 붙는 말
                                followUps = data.followUpMessages ?? [];
                                // 대화 칩(C-29 · D74): 답 뒤에 단골이 보낼 말
                                chips = data.chips ?? null;
                            } else if (name === 'stopped') {
                                if (data.saved) {
                                    handlers.onMemberMessage(data.message);
                                } else {
                                    handlers.onRestoreDraft(data.text, acceptedMessagesId);
                                }
                            } else if (name === 'error') {
                                setError(data.error);
                                handlers.onResync();
                            }
                        },
                        onCut() {
                            setError('답이 끊겼어요. 다시 보내 주세요.');
                            handlers.onResync();
                        },
                    }
                );
                // 같은 멤버 연속 말풍선: 「입력 중」을 띄우고 0.8~1.2초 × 말 속도 뒤 하나씩
                for (const followUp of followUps) {
                    setPhase('thinking');
                    await wait(
                        revealGapOf({ body: lastShown?.body, memberKey }, { body: followUp.body, memberKey }),
                        controller.signal
                    );
                    if (controller.signal.aborted) {
                        break;
                    }
                    handlers.onMemberMessage(followUp);
                    lastShown = followUp;
                }
                if (chips !== undefined && !controller.signal.aborted) {
                    handlers.onChips?.(chips);
                }
            } catch (requestError) {
                // 칩이 바뀐 뒤 누른 칩(23.4): 보내지 않고 새 묶음
                if (requestError?.code === 'CHIP_STALE') {
                    pendingRef.current = null;
                    handlers.onChips?.(requestError.details?.chips ?? null);
                } else if (!controller.signal.aborted) {
                    setError(errorMessageOf(requestError, '보내지 못했어요. 다시 보내 주세요.'));
                    handlers.onResync();
                }
            } finally {
                if (controllerRef.current === controller) {
                    controllerRef.current = null;
                }
                setPhase('idle');
                setPendingText(null);
                setStreamText('');
            }
        },
        [memberKey]
    );

    const stop = useCallback(async () => {
        if (!controllerRef.current) {
            return;
        }
        try {
            await stopChatTurn(memberKey);
        } catch (stopError) {
            // 멈추기 요청이 안 닿으면 연결을 끊습니다. 서버는 끊김을 중단으로 받습니다.
            console.error('[chat] 멈추기 요청 실패', stopError?.code ?? stopError?.name);
            controllerRef.current?.abort();
            handlersRef.current.onResync();
        }
    }, [memberKey]);

    return { phase, streamText, pendingText, error, send, stop, generating: phase !== 'idle' };
}
